import React, { useState, useRef } from 'react';
import Img, { FixedObject, FluidObject } from 'gatsby-image';

import '../../assets/images/homepage-diagram/global-animations.scss';
import '../../assets/images/homepage-diagram/de/animations.scss';
import '../../assets/images/homepage-diagram/es/animations.scss';
import '../../assets/images/homepage-diagram/fr/animations.scss';
import '../../assets/images/homepage-diagram/ja/animations.scss';
import '../../assets/images/homepage-diagram/nl/animations.scss';
import '../../assets/images/homepage-diagram/pt-br/animations.scss';
import '../../assets/images/homepage-diagram/en/animations.scss';

import './value-prop.scss';
import {
  ImageSharpConnection,
  ImageSharp,
  File,
  Drupal_ParagraphCta,
  Drupal_FieldNodeHomepageFieldValuePropCard
} from '../../generated/graphql';
import { isDefined } from '../../types/types';
import { useEffect } from 'react';

interface ValuePropProps {
  intro: Drupal_ParagraphCta;
  cards: Drupal_FieldNodeHomepageFieldValuePropCard[];
  mobile: ImageSharpConnection;
  tablet: ImageSharpConnection;
  desktop: string | null | undefined;
}

const ValueProp = ({
  intro,
  cards,
  mobile,
  tablet,
  desktop
}: ValuePropProps) => {
  const svgStates = ['simple', 'collaborative', 'data'];
  const diagramParentRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const diagramWrap = diagramParentRef.current;
    if (diagramWrap) {
      const svg = diagramWrap.childNodes[0] as HTMLElement;
      if (svg) {
        svg.dataset.active = svgStates[activeIndex];
      }
    }
  }, [activeIndex]);

  const clickDiagram = () => {
    let newIndex = activeIndex;
    if (activeIndex < svgStates.length - 1) {
      newIndex += 1;
    } else {
      newIndex = 0;
    }
    setActiveIndex(newIndex);
  };

  const mobileImages = mobile.edges
    .map(({ node }) => node as File)
    .map(({ childImageSharp }) => childImageSharp as ImageSharp)
    .filter(isDefined);

  const tabletImages = tablet.edges
    .map(({ node }) => node as File)
    .map(({ childImageSharp }) => childImageSharp as ImageSharp)
    .filter(isDefined);

  return (
    <section className="value-prop">
      <div className="value-prop__header lucid-container lucid-container--flex padding-t-sm padding-b-sm">
        <h2 className="value-prop__title no-margin">{intro.fieldCtaHeading}</h2>
        {intro.fieldCtaLink && intro.fieldCtaLink.url && (
          <a
            className="value-prop__link button chart chart--primary hide-mobile"
            href={intro.fieldCtaLink.url.path!}
          >
            {intro.fieldCtaLink.title}
          </a>
        )}
      </div>

      <div className="value-prop__diagram-background show--desktop--flex hide-tablet hide-mobile">
        <div className="lucid-container">
          <button
            type="button"
            className="value-prop__diagram diagram"
            onClick={() => clickDiagram()}
          >
            <div
              dangerouslySetInnerHTML={{ __html: desktop! }}
              ref={diagramParentRef}
            />
          </button>
        </div>
      </div>

      <div className="js-valueProp">
        {cards && (
          <div
            className="lucid-container
              lucid-container--flex
              padding-b-sm-mobile"
          >
            {cards.map(({ entity }, i) => {
              const {
                fieldCtaHeading,
                fieldDescription,
                fieldCtaLink
              } = entity as Drupal_ParagraphCta;
              return (
                <div className="value-prop__card-thirds" key={fieldCtaHeading!}>
                  <div
                    className="value-prop__diagram-background  value-prop__diagram-background--show-mobile"
                    key={fieldCtaHeading!}
                  >
                    <Img
                      alt={fieldCtaHeading!}
                      fluid={mobileImages[i].fluid as FluidObject}
                      className="value-prop__diagram-image"
                    />
                  </div>
                  <div className="value-prop__diagram-background  value-prop__diagram-background--show-tablet">
                    <Img
                      alt={fieldCtaHeading!}
                      fixed={tabletImages[i].fixed as FixedObject}
                      className="value-prop__diagram-image"
                    />
                  </div>
                  <button
                    type="button"
                    className={`value-prop__card  card  ${
                      i === activeIndex
                        ? 'value-prop__card--raised card--raised'
                        : ''
                    } padding-b-md  padding-t-md`}
                    onClick={() => setActiveIndex(i)}
                  >
                    <h3 className="value-prop__card-title">
                      {fieldCtaHeading}
                    </h3>
                    <p className="value-prop__card-description">
                      {fieldDescription}
                    </p>
                    <a
                      className="value-prop__card-button"
                      href={fieldCtaLink!.url!.path!}
                    >
                      <span className="value-prop__card-button-text">
                        {fieldCtaLink!.title}
                      </span>
                      <span className="value-prop__arrow">→</span>
                    </a>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ValueProp;
